import { Anomaly, AnomalyRule, Timeframe } from './api';

/**
 * snake_or_kebab_case → Title Case
 * "daily_active_scanners" → "Daily Active Scanners"
 */
export function humanizeName(name: string | null | undefined): string {
  if (!name) return '—';
  return name
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .map((w) =>
      w.length <= 2
        ? w.toUpperCase()
        : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
    )
    .join(' ');
}

export const TIMEFRAME_LABEL: Record<Timeframe, string> = {
  DAY: 'Day-over-day',
  WEEK: 'Week-over-week',
  MONTH: 'Month-over-month',
  YEAR: 'Year-over-year'
};

/** What the figure is actually being compared against, in words.
 *
 * "versus the prior period" is the system's phrase for its own windowing, not
 * an answer to "compared to what?". The card already shows the two dates; this
 * says which two they are.
 */
export const COMPARED_WITH: Record<Timeframe, string> = {
  DAY: 'compared with the day before',
  WEEK: 'compared with the week before',
  MONTH: 'compared with the month before',
  YEAR: 'compared with the year before'
};

export const TIMEFRAME_SHORT: Record<Timeframe, string> = {
  DAY: 'Daily',
  WEEK: 'Weekly',
  MONTH: 'Monthly',
  YEAR: 'Yearly'
};

/** The comparison as the server worded it, falling back to the timeframe
 *  wording for a platform build that does not send one. A rule set to
 *  year-over-year must never be labelled "day-over-day". */
export function comparisonLabel(a: Anomaly): string {
  return a.presentation?.comparison?.label ?? TIMEFRAME_LABEL[a.timeframe];
}

export function comparedWith(a: Anomaly): string {
  return (
    a.presentation?.comparison?.compared_with ?? COMPARED_WITH[a.timeframe]
  );
}

export function comparisonNotes(a: Anomaly): string[] {
  return a.presentation?.comparison?.notes ?? [];
}

/** Year-over-year periods share a month and day, so they need the year. */
export function isYearOverYear(a: Anomaly): boolean {
  return a.presentation?.comparison?.basis === 'yoy_calendar';
}

/** ISO timestamp → "2 hours ago" / "yesterday" / "3 days ago" */
export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const now = Date.now();
  const diffMs = now - then;
  const s = Math.max(1, Math.round(diffMs / 1000));
  const m = Math.round(s / 60);
  const h = Math.round(m / 60);
  const d = Math.round(h / 24);
  if (s < 60) return 'just now';
  if (m < 60) return `${m} minute${m === 1 ? '' : 's'} ago`;
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  if (d === 1) return 'yesterday';
  if (d < 30) return `${d} days ago`;
  const months = Math.round(d / 30);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
  const years = Math.round(d / 365);
  return `${years} year${years === 1 ? '' : 's'} ago`;
}

/** Absolute local timestamp e.g. "Jul 26, 11:12" — for tooltips / details. */
export function absoluteTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return iso;
  }
}

/** 12437.6 → "12.4K" · 1502341 → "1.5M" · 0.42 → "0.42" */
export function formatValue(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (abs >= 1_000_000_000)
    return `${sign}${(abs / 1_000_000_000).toFixed(2)}B`;
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sign}${(abs / 1_000).toFixed(2)}K`;
  if (abs >= 10) return v.toFixed(0);
  if (abs >= 1) return v.toFixed(2);
  return v.toFixed(3);
}

/** 45.3 → "45%" (rounds to whole percent for scan-ability) */
export function formatPct(
  v: number | null | undefined,
  opts: { signed?: boolean } = {}
): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  const rounded = Math.abs(v) >= 10 ? Math.round(v) : Number(v.toFixed(1));
  const sign = opts.signed && v > 0 ? '+' : '';
  return `${sign}${rounded}%`;
}

/**
 * Human sentence describing what a rule watches for.
 *
 * PCT_CHANGE examples:
 *   "Watches changes: daily ≥50%, weekly ≥30%, monthly ≥20%"
 *   "Watches drops only: daily ≥40%"
 *   "Watches increases only: weekly ≥25%"
 *
 * SAME_WEEKDAY_ZSCORE example:
 *   "Watches daily deviations from the last 8 same-weekdays (≥3σ)"
 */
export function describeRule(rule: AnomalyRule): string {
  const cfg = (rule.config ?? {}) as Record<string, unknown>;

  if (rule.rule_type === 'SAME_WEEKDAY_ZSCORE') {
    const z = typeof cfg.z_threshold === 'number' ? cfg.z_threshold : 3.0;
    const weeks =
      typeof cfg.weeks_lookback === 'number' ? cfg.weeks_lookback : 8;
    return `Watches daily deviations from the last ${weeks} same-weekdays (≥${z}σ)`;
  }

  // PCT_CHANGE
  const thresholds =
    cfg.thresholds && typeof cfg.thresholds === 'object'
      ? (cfg.thresholds as Record<string, number>)
      : {};
  const parts = rule.timeframes
    .filter((tf) => typeof thresholds[tf] === 'number')
    .map((tf) => `${TIMEFRAME_SHORT[tf].toLowerCase()} ≥${thresholds[tf]}%`);

  const verb =
    rule.direction === 'UP'
      ? 'Watches increases only'
      : rule.direction === 'DOWN'
        ? 'Watches drops only'
        : 'Watches changes';

  if (parts.length === 0) return `${verb} (no thresholds set)`;
  return `${verb}: ${parts.join(', ')}`;
}

/**
 * The period an anomaly compares, as a person writes a date.
 *
 * Periods are stored as [start, end) at UTC midnight. Rendering them with
 * toLocaleString gave "Aug 25, 03:00 AM – Aug 26, 03:00 AM" for a single day
 * in Cairo (+3): three numbers and a time nobody asked about, for what is
 * simply "Aug 25". Formatted in UTC, date-only, with the end read as
 * inclusive.
 */
export function periodLabel(
  startIso: string | null | undefined,
  endIso: string | null | undefined,
  timeframe: Timeframe,
  withYear = false
): string {
  if (!startIso) return '—';
  const start = new Date(startIso);
  if (Number.isNaN(start.getTime())) return startIso;

  const day = (d: Date) =>
    `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}` +
    (withYear ? `, ${d.getUTCFullYear()}` : '');

  if (timeframe === 'MONTH')
    return `${MONTHS[start.getUTCMonth()]} ${start.getUTCFullYear()}`;
  if (timeframe === 'YEAR') return String(start.getUTCFullYear());
  if (timeframe === 'DAY') return day(start);

  // WEEK — the stored end is the exclusive next boundary.
  if (!endIso) return day(start);
  const end = new Date(endIso);
  if (Number.isNaN(end.getTime())) return day(start);
  const lastDay = new Date(end.getTime() - 86_400_000);
  return `${day(start)} – ${day(lastDay)}`;
}

/**
 * The same period as plain inclusive ISO dates, for handing to the chat.
 *
 * Stored periods are half-open — `[start, end)` — so a one-day finding on
 * 1 September is stored as 2026-09-01 → 2026-09-02. Passing those two dates
 * to the model as if they were a range made it compare 1 September against
 * 2 September and answer −87% to a card that said +1055%: Signals
 * contradicting its own alert, in front of the customer who clicked it.
 *
 * Returns `to === from` for a single-day period, so the caller can say
 * "on the 1st" rather than "from the 1st to the 1st".
 */
export function isoPeriodRange(
  startIso: string | null | undefined,
  endIso: string | null | undefined
): { from: string; to: string } | null {
  if (!startIso) return null;
  const start = new Date(startIso);
  if (Number.isNaN(start.getTime())) return null;
  const from = start.toISOString().slice(0, 10);

  if (!endIso) return { from, to: from };
  const end = new Date(endIso);
  if (Number.isNaN(end.getTime())) return { from, to: from };
  // The stored end is the exclusive next boundary; the last day inside the
  // period is the day before it.
  const lastDay = new Date(end.getTime() - 86_400_000);
  const to = lastDay.toISOString().slice(0, 10);
  return { from, to: to < from ? from : to };
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];

/**
 * Whether a stored driver may be quoted as a SHARE of the movement.
 *
 * The driver is computed by grouping the metric over a dimension, but it does
 * not replay the extra conditions a rule's own SQL may carry — so on some rules
 * its total lands on a different quantity than the anomaly's own delta, and the
 * card ended up printing three different numbers: "up 62% ... a change of
 * 95.03K ... +2.37M of 325.44K — 727% of the change". Quote the share only
 * when the driver measured the same movement the card is reporting.
 */
export function driverShareIsTrustworthy(
  driver:
    | { total_change?: number | null; share_pct?: number | null }
    | null
    | undefined,
  anomalyDelta: number | null | undefined
): boolean {
  if (!driver || driver.total_change == null || driver.share_pct == null)
    return false;
  if (anomalyDelta == null || anomalyDelta === 0) return false;
  return (
    Math.abs(driver.total_change - anomalyDelta) <= Math.abs(anomalyDelta) * 0.2
  );
}

/**
 * A mover can account for more than the whole net change when other dimensions
 * moved the other way (one branch −3.95M against a net −3.74M = 106%). That is
 * a real fact about the business and worth stating, but "106% of the change"
 * reads as a mistake — so those get their own sentence.
 */
export function driverOffsetByOthers(
  driver:
    | { share_pct?: number | null; offset_by_others?: boolean }
    | null
    | undefined
): boolean {
  if (!driver) return false;
  if (typeof driver.offset_by_others === 'boolean')
    return driver.offset_by_others;
  return driver.share_pct != null && Math.abs(driver.share_pct) > 110;
}

/**
 * What the finding is worth, in the reader's language.
 *
 * "Down 42.9%" is correct and moves nobody; "3.74M EGP, 11% of a normal month"
 * is the same fact translated into a decision. Both sentences are rendered
 * server-side by `anomaly_impact.py`, so this only picks one — the client never
 * computes money, and never fills in a figure the server left out.
 *
 * Returns null for a platform build that predates the field, which the card
 * treats as "show the percentage alone", exactly today's behaviour.
 */
export function impactText(anomaly: Anomaly, language?: string): string | null {
  const impact = anomaly.presentation?.impact;
  if (!impact) return null;
  return language?.toLowerCase().startsWith('ar')
    ? impact.sentence.ar
    : impact.sentence.en;
}

/**
 * Whether this finding's size could be measured at all.
 *
 * False is not an error and not a zero — it is a metric with no stable monthly
 * scale, and the card says so in words rather than showing a confident number
 * nobody can act on. Worth styling differently from a measured finding so the
 * difference is visible at a glance.
 */
export function impactScaleKnown(anomaly: Anomaly): boolean {
  return anomaly.presentation?.impact?.scale_known ?? false;
}

/**
 * Which group a finding belongs to, and what to call that group.
 *
 * The bands are decided on the server, from the same cut-offs the executor uses
 * to cap how severe a movement may claim to be. Deciding them here would let
 * the page group a finding as major while the backend had graded it MEDIUM for
 * being too small — one number disagreeing with itself on one card.
 */
export function impactBand(anomaly: Anomaly): string | null {
  return anomaly.presentation?.impact?.band ?? null;
}

export function impactBandLabel(
  anomaly: Anomaly,
  language?: string
): string | null {
  const label = anomaly.presentation?.impact?.band_label;
  if (!label) return null;
  return language?.toLowerCase().startsWith('ar') ? label.ar : label.en;
}

/** The order bands are shown in. `unmeasured` sits last: a finding whose size
 *  could not be established is not a small one, and putting it among the trivia
 *  would answer a question the data refused to answer. */
export const BAND_ORDER = [
  'major',
  'significant',
  'moderate',
  'minor',
  'unmeasured'
];

/**
 * Split findings into their bands, in order, dropping empty ones.
 *
 * Takes a band extractor rather than an Anomaly list, because the page groups
 * ALREADY-collapsed stories (one card per metric, the strongest finding
 * leading) and not raw rows.
 *
 * Order WITHIN each band is left exactly as the server returned it. The list is
 * keyset-paginated, and re-sorting a page locally is the bug this replaced.
 */
export function groupByImpact<T>(
  items: T[],
  bandOf: (item: T) => string | null
): { band: string; items: T[] }[] {
  const buckets = new Map<string, T[]>();
  for (const item of items) {
    const band = bandOf(item) ?? 'unmeasured';
    const bucket = buckets.get(band);
    if (bucket) bucket.push(item);
    else buckets.set(band, [item]);
  }
  return BAND_ORDER.filter((b) => buckets.has(b)).map((band) => ({
    band,
    items: buckets.get(band) as T[]
  }));
}
